export const OPTOMETRIST_QUESTIONS = [
  {
    index: 0,
    category: "Binocular balancing",
    question: "What is the purpose of binocular balancing at the end of a subjective refraction and what is the maximum dioptre the patient can tolerate?",
    model_answer: {
      points: [
        "Binocular balancing ensures equal accommodative effort in both eyes to prevent one eye from driving accommodation preferentially.",
        "It equalises the blur stimulus between the two eyes so neither eye is over- or under-corrected relative to the other.",
        "Maximum tolerance: 2.00 to 2.50 Dioptres",
      ],
    },
  },
  {
    index: 1,
    category: "Binocular balancing",
    question: "A patient achieves VA of 6/6 with -2.50/-1.00 x 150 in the right eye and VA of 6/60 with -8.00/-4.00 x 120 in the left eye. What prescription will you give this patient?",
    model_answer: {
      table: {
        headers: ["Eye", "Sphere", "Cylinder", "Axis", "VA"],
        rows: [
          ["R", "-2.50", "-1.00", "x 150", "6/6"],
          ["L", "-4.50", "-2.00", "x 120", "6/60"],
        ],
      },
      points: [
        "The right eye is prescribed as refracted. The left eye is given half the sphere and half the cylinder correction to avoid over-minusing the amblyopic/high ametropic eye, preventing aniseikonia and adaptation issues.",
      ],
    },
  },
  {
    index: 2,
    category: "Reading add",
    question: "A 52-year-old patient has a distance prescription of +1.50 DS. They work at a habitual reading distance of 40 cm. What is the typical reading add by age?",
    model_answer: {
      table: {
        headers: ["Age range", "Typical reading add"],
        rows: [
          ["40 – 44", "+0.75 – +1.00 D"],
          ["45 – 49", "+1.00 – +1.50 D"],
          ["50 – 54", "+1.50 – +2.00 D"],
          ["55 – 59", "+2.00 – +2.50 D"],
          ["60 +",    "+2.50 – +3.00 D"],
        ],
      },
      summary: "For age 50–54: +1.50 to +2.00 D",
    },
  },
  {
    index: 3,
    category: "Reading add",
    question: "When prescribing a reading addition, what factors beyond the calculated add would you consider before finalising the near prescription?",
    model_answer: {
      points: [
        "Working distance and specific task requirements.",
        "Occupational needs and intermediate visual demands.",
        "Previous add tolerance and adaptation history.",
        "Frame and lens type — single vision, progressive, or bifocal.",
        "Whether the add causes any symptoms at the patient's habitual near distance.",
      ],
    },
  },
  {
    index: 4,
    category: "Pathology",
    question: "During fundus examination you observe a cup-to-disc ratio of 0.8 with inferior notching and flame haemorrhages at the disc margin. The patient's IOP is 24 mmHg. What is your differential diagnosis and immediate management plan?",
    model_answer: {
      points: [
        "Primary differential: primary open-angle glaucoma. Also consider ocular hypertension with suspicious disc.",
        "Measure IOP in both eyes, perform visual field assessment, OCT of RNFL, and central corneal thickness measurement.",
        "Refer urgently to ophthalmology. Do not discharge without follow-up.",
      ],
    },
  },
  {
    index: 5,
    category: "Pathology",
    question: "A 65-year-old patient comes in complaining about fuzzy vision and presents with VA 6/45 and 6/60 uncorrected. After subjective refraction VA changes to 6/24 and 6/60. Pinhole brings no improvement. Give the differential diagnoses as well as a treatment plan.",
    model_answer: {
      points: [
        "Differential diagnosis: Cataract and/or macular degeneration — both can cause reduced VA that does not improve with pinhole.",
        "Treatment plan: Write a referral letter to ophthalmology for further investigation and surgical assessment.",
        "Prescribe spectacles in the interim — as the state is behind on referrals and the refraction improved the patient's vision, glasses will provide meaningful benefit while awaiting specialist care.",
      ],
      summary: "Any of: cataract, macular degeneration, referral letter, prescribing glasses — accepted as correct",
    },
  },
  {
    index: 6,
    category: "Paediatric",
    question: "A child has a VA of 6/6 in both eyes and presents with headaches and a decreased stereo test. In your opinion will this child require spectacles and what type of prescription would they likely require?",
    model_answer: {
      points: [
        "Yes — a decreased stereo test with VA of 6/6 is typically associated with an accommodative disorder or high hypermetropia.",
        "The child is likely compensating through accommodation to achieve 6/6, causing the headaches and reduced stereopsis.",
        "Spectacles are indicated — the prescription would likely be a hypermetropic (plus) correction to relieve the accommodative strain.",
      ],
      summary: "Expected Rx type: Hypermetropic (plus) prescription",
    },
  },
  {
    index: 7,
    category: "Optical centre",
    question: "An 18-year-old patient complains of fuzzy vision and difficulty seeing the board. Subjective refraction: R +2.00/-4.50 x 150 VA 6/7.5   L +1.50/-6.00 x 120 VA 6/7.5. What prescription will you give them?",
    model_answer: {
      points: [
        "As first-time wearers with high cylinders, give half the cylinder to aid adaptation — always check what the patient is comfortable with.",
      ],
      table: {
        headers: ["Eye", "Sphere", "Cylinder", "Axis"],
        rows: [
          ["R", "+2.00", "-2.25", "x 150"],
          ["L", "+1.50", "-3.00", "x 120"],
        ],
      },
      summary: "Half the cylinder prescribed as patient is a first-time wearer",
    },
  },
  {
    index: 8,
    category: "High prescription",
    question: "An 18-year-old patient complains of fuzzy vision and difficulty seeing the board. This is his first eye test. Subjective refraction: R +2.00/−4.50 × 150 VA 6/7.5 | L +1.50/−6.00 × 120 VA 6/7.5. What prescription will you give them and why?",
    model_answer: {
      points: [
        "This is a first prescription with high astigmatism — careful dispensing is required.",
        "The patient is 18 and symptomatic (fuzzy vision, difficulty seeing the board), which supports giving the full correction.",
        "Full prescription: R +2.00/−4.50 × 150 | L +1.50/−6.00 × 120.",
        "Alternatively, reduce the cylinder on the first prescription (e.g. by 25–50%) and adjust the sphere to maintain the spherical equivalent, to aid adaptation.",
        "If cylinder is reduced: Spherical equivalent = sphere + (cylinder ÷ 2). E.g. R: SE = +2.00 + (−4.50 ÷ 2) = −0.25. Reduced Rx could be R −0.25/−3.00 × 150 | L −1.50/−4.00 × 120 (approx).",
        "VA of 6/7.5 with full correction — consider whether longstanding uncorrected astigmatism has caused meridional amblyopia.",
        "Counsel the patient that adaptation may take 1–2 weeks, especially with the high cylinders.",
        "Review in 4–6 weeks to assess adaptation and update if needed.",
      ],
      summary: "Full correction is defensible given age and symptoms. Reduced cylinder at first issue is also acceptable — must justify with spherical equivalent calculation. Either answer accepted if reasoning is sound.",
      note: "Award marks for clinical reasoning, spherical equivalent understanding, and patient counselling — not just the final Rx values.",
    },
  },
  {
    index: 9,
    category: "Optical centre",
    question: "Do you need to mark the optical centre of a high prescription lens? True or False.",
    model_answer: {
      points: [
        "True — high prescription lenses must have the optical centre precisely marked and aligned to the patient's pupillary distance.",
        "Any decentration in a high-power lens produces a significant prismatic effect (Prentice's Rule), causing distortion, eye strain, and potentially diplopia.",
      ],
      summary: "TRUE",
    },
  },
];

export const DISPENSER_QUESTIONS = [
  {
    index: 0,
    category: "Binocular balancing",
    question: "What is the purpose of binocular balancing at the end of a subjective refraction and what is the maximum dioptre the patient can tolerate?",
    model_answer: {
      points: [
        "Binocular balancing ensures equal accommodative effort in both eyes to prevent one eye from driving accommodation preferentially.",
        "It equalises the blur stimulus so neither eye is over- or under-corrected relative to the other.",
        "Maximum tolerance: 2.00 to 2.50 Dioptres",
      ],
    },
  },
  {
    index: 1,
    category: "Binocular balancing",
    question: "A patient achieves VA of 6/6 with -2.50/-1.00 x 150 in the right eye and VA of 6/60 with -8.00/-4.00 x 120 in the left eye. As an optical dispenser, what prescription would you expect to receive from the optometrist to dispense, and why?",
    model_answer: {
      points: [
        "Right eye prescribed as refracted: -2.50/-1.00 x 150",
        "Left eye receives half the correction to prevent aniseikonia: -4.50/-2.00 x 120",
        "Consider a high-index lens for the left eye to reduce thickness and weight.",
      ],
      summary: "R: -2.50/-1.00 x 150  |  L: -4.50/-2.00 x 120",
    },
  },
  {
    index: 2,
    category: "Reading add",
    question: "A 52-year-old patient has a distance prescription of +1.50 DS. They work at a habitual reading distance of 40 cm. What is the typical reading add by age and what would you expect for this patient?",
    model_answer: {
      table: {
        headers: ["Age range", "Typical reading add"],
        rows: [
          ["40 – 44", "+0.75 – +1.00 D"],
          ["45 – 49", "+1.00 – +1.50 D"],
          ["50 – 54", "+1.50 – +2.00 D"],
          ["55 – 59", "+2.00 – +2.50 D"],
          ["60 +",    "+2.50 – +3.00 D"],
        ],
      },
      summary: "For age 50–54: +1.50 to +2.00 D  |  Near Rx = +3.00 to +3.50 DS",
    },
  },
  {
    index: 3,
    category: "Reading add",
    question: "A patient has the following distance prescription: R +1.00/-0.50 x 90   L +1.25/-0.75 x 85   Add: +2.00. Calculate the full near prescription for both eyes.",
    model_answer: {
      note: "Add the reading add to the sphere of each eye",
      table: {
        headers: ["Eye", "Near sphere", "Cylinder", "Axis"],
        rows: [
          ["R", "+3.00", "-0.50", "x 90"],
          ["L", "+3.25", "-0.75", "x 85"],
        ],
      },
      points: ["Add +2.00 is added to the sphere only. Cylinder and axis remain unchanged."],
      summary: "R near: +3.00/-0.50 x 90  |  L near: +3.25/-0.75 x 85",
    },
  },
  {
    index: 4,
    category: "Reading add",
    question: "A 57-year-old myope has a distance prescription of R -3.50/-0.75 x 170   L -4.00/-1.00 x 10   Add: +2.25. Calculate the full near prescription and explain what is unique about dispensing a reading add for a myope.",
    model_answer: {
      table: {
        headers: ["Eye", "Near sphere", "Cylinder", "Axis"],
        rows: [
          ["R", "-1.25", "-0.75", "x 170"],
          ["L", "-1.75", "-1.00", "x 10"],
        ],
      },
      points: [
        "-3.50 + 2.25 = -1.25  |  -4.00 + 2.25 = -1.75. Cylinder and axis unchanged.",
        "A myope may find they can remove their glasses to read comfortably. Discuss whether a bifocal or progressive is truly needed for their lifestyle.",
      ],
      summary: "R near: -1.25/-0.75 x 170  |  L near: -1.75/-1.00 x 10",
    },
  },
  {
    index: 5,
    category: "Reading add",
    question: "When prescribing a reading addition, what factors beyond the calculated add would you as a dispenser consider before finalising the near lens choice?",
    model_answer: {
      points: [
        "Working distance and task requirements — desk, book, or screen.",
        "Lens type — single vision near, bifocal, or progressive.",
        "Previous add tolerance and multifocal history.",
        "Frame size and shape — corridor length for progressives.",
        "Whether intermediate vision is also required.",
      ],
    },
  },
  {
    index: 6,
    category: "High prescription",
    question: "An 18-year-old first-time wearer presents with R +2.00/-4.50 x 150 VA 6/7.5 and L +1.50/-6.00 x 120 VA 6/7.5. Before starting to cut the lenses you notice this patient has never worn glasses before. What would your next step be?",
    model_answer: {
      points: [
        "Do not proceed to cut the lenses immediately — contact the optometrist first.",
        "Ask the optometrist whether they should rather prescribe half the cylinder for comfort and adaptation as a first-time wearer.",
        "Once confirmed, advise the patient that a reduced cylinder has been dispensed to ease adaptation, with full prescription at the next visit.",
      ],
      summary: "Always check with the optometrist before cutting high cyl for a first-time wearer",
    },
  },
  {
    index: 7,
    category: "Reading add",
    question: "A 48-year-old myope has a distance prescription of R -6.00 DS   L -5.50 DS   Add: +1.25. Calculate the near prescription and explain whether this patient may need their near glasses at all and why.",
    model_answer: {
      table: {
        headers: ["Eye", "Near sphere", "Cylinder", "Axis"],
        rows: [
          ["R", "-4.75", "—", "—"],
          ["L", "-4.25", "—", "—"],
        ],
      },
      points: [
        "-6.00 + 1.25 = -4.75  |  -5.50 + 1.25 = -4.25. No cylinder present.",
        "A moderate-to-high myope can often remove their distance glasses and read clearly without a near addition. Discuss whether a bifocal or progressive is necessary for this patient's lifestyle.",
      ],
      summary: "R near: -4.75 DS  |  L near: -4.25 DS",
    },
  },
  {
    index: 8,
    category: "Optical centre",
    question: "Explain why the optical centre of a lens is important when fitting lenses.",
    model_answer: {
      points: [
        "Incorrect centration causes distortions in the patient's vision.",
        "A decentred optical centre induces a prismatic effect (Prentice's Rule), causing eye strain or diplopia.",
        "Poor centration increases eye strain and may lead to headaches and visual discomfort.",
      ],
      summary: "Any one of: distortions, prismatic effect, or increased eye strain — accepted",
    },
  },
  {
    index: 9,
    category: "Optical centre",
    question: "Do you need to mark the optical centre of a high prescription lens? True or False.",
    model_answer: {
      points: [
        "True — high prescription lenses must have the optical centre precisely marked and aligned to the patient's PD.",
        "Any decentration produces a significant prismatic effect, causing distortion, eye strain, and potentially diplopia.",
      ],
      summary: "TRUE",
    },
  },
];

export const SCORECARD_SECTIONS = [
  {
    key: "interview",
    label: "Interview",
    max: 150,
    groups: [
      {
        label: "Interview content",
        questions: [
          { key: "i1", label: "How clearly and confidently did the candidate communicate their experience?" },
          { key: "i2", label: "Did they demonstrate strong problem-solving ability with examples?" },
          { key: "i3", label: "How well do they align with the company's values and culture?" },
          { key: "i4", label: "Did they ask thoughtful, relevant questions about the role?" },
          { key: "i5", label: "How strong was their overall first impression and professionalism?" },
          { key: "i6", label: "Was the candidate able to explain what is Hypermetropia?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "i7", label: "Was the candidate able to explain what is Myopia?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "i8", label: "Was the candidate able to explain what is Astigmatism?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "i9", label: "Was the candidate able to explain what is Presbyopia?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "i10", label: "Was the candidate able to explain what is Anisometropia?", yesNo: true, yesScore: 10, noScore: 0 },
        ],
      },
      {
        label: "Tech readiness & logistics",
        questions: [
          { key: "t1", label: "Was the candidate able to log into Teams independently without any assistance?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "t2", label: "Did you need to phone, chase, or remind them about the interview beforehand?", yesNo: true, yesScore: 0, noScore: 10 },
          { key: "t3", label: "Was the candidate presentable and willing to switch on their camera?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "t4", label: "Did they join the call on time and manage the technology confidently?", yesNo: true, yesScore: 10, noScore: 0 },
          { key: "t5", label: "Overall impression — how ready do they seem to work in a tech-driven environment?" },
        ],
      },
    ],
  },
  {
    key: "references",
    label: "Reference Checks",
    max: 50,
    groups: [
      {
        label: null,
        questions: [
          { key: "r1", label: "Did references describe the candidate as reliable and consistent?" },
          { key: "r2", label: "How positively did references rate their teamwork and collaboration?" },
          { key: "r3", label: "Did references confirm strong work ethic and initiative?" },
          { key: "r4", label: "Were references willing to rehire or work with this candidate again?" },
          { key: "r5", label: "Did references highlight any outstanding achievements or contributions?" },
        ],
      },
    ],
  },
  {
    key: "test",
    label: "Test Results",
    max: 10,
    singleScore: true,
    groups: [
      {
        label: null,
        questions: [
          { key: "score", label: "Score entered by admin after marking the candidate's online test. Use the separate test marking tool and enter the final score here." },
        ],
      },
    ],
  },
  {
    key: "work_experience",
    label: "Work Experience & Background",
    max: 50,
    groups: [
      {
        label: null,
        questions: [
          { key: "w1", label: "Do they have credible, verifiable references from previous employers?" },
          { key: "w2", label: "Are they able to work effectively as part of a team?" },
          { key: "w3", label: "Are they adjustable and able to cope when things change at the last minute?" },
          { key: "w4", label: "How do they handle working with authority figures and management?" },
          { key: "w5", label: "Would the previous employer hire this person again if they had the opportunity?" },
        ],
      },
    ],
  },
  {
    key: "culture",
    label: "Culture & Values Fit",
    max: 40,
    groups: [
      {
        label: null,
        questions: [
          { key: "c2", label: "Did they show genuine enthusiasm for the company's mission?" },
          { key: "c3", label: "How well do their personal values align with the organisation's?" },
          { key: "c4", label: "Did they demonstrate respect, empathy, and emotional intelligence?" },
          { key: "c5", label: "Would existing team members likely enjoy working alongside them?" },
        ],
      },
    ],
  },
];
